import React, { useState } from 'react';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Checkbox,
    FormControlLabel,
    Box,
    Typography,
    Stack,
    InputLabel,
    FormControl
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers';
import 'react-widgets/scss/styles.scss';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { errorMessage } from 'utils/constants';
import DisplayOtc from './DisplayOtc';
import { getMinimumSelectableDate } from 'utils/Helpers';
import { OtcHelper } from '../../utils/helpers/otc.helper';
import OrgService from '../../services/org.service';
import SnackbarNotify from '../../utils/snackbar/SnackbarNotify';
import { Loader } from '../index';
import AnimateButton from '../@extended/AnimateButton';

const EditOtcModal = ({ onClose, otc }) => {
    const [loading, setLoading] = useState(false);
    const [updatedOtc, setUpdatedOtc] = useState(null);

    const submit = async values => {
        const payload = {
            reusable: values.reusable
        };

        if (values.activation_date) {
            payload.starts_at = OtcHelper.toFirestoreTimestamp(values.activation_date);
        }

        if (values.reusable) {
            payload.expires_at = OtcHelper.toFirestoreTimestamp(values.expiration_date);
        } else {
            const startsAt = new Date(values.activation_date);
            const expiresAt = new Date(startsAt);
            expiresAt.setDate(startsAt.getDate() + 30);

            payload.expires_at = OtcHelper.toFirestoreTimestamp(expiresAt.toISOString());
        }

        try {
            setLoading(true);
            await OrgService.updateOrgOtc(otc.org_id, otc.code_id, payload);

            const response = await OrgService.getOrgOtcById(otc.org_id, otc.code_id);
            setUpdatedOtc(response);
        } catch (error) {
            console.error('Failed to update OTC:', error);
            SnackbarNotify.error(
                `Failed to update OTC: ${error?.message || 'Unexpected error'}`
            );
        } finally {
            setLoading(false);
        }
    };

    const validationSchema = Yup.object().shape({
        activation_date: Yup.date().required(errorMessage.toDate.required),
        expiration_date: Yup.date().when('reusable', {
            is: true,
            then: schema =>
                schema
                    .required(errorMessage.toDate.required)
                    .test(
                        'expiration_at_least_one_hour_after_activation',
                        'Expiration date must be at least 1 hour from activation date',
                        function (value) {
                            const { activation_date } = this.parent;
                            if (!activation_date || !value) return false;

                            const oneHourLater = new Date(activation_date);
                            oneHourLater.setHours(oneHourLater.getHours() + 1);

                            return value.getTime() >= oneHourLater.getTime();
                        }
                    )
        }),
        reusable: Yup.boolean()
    });

    const activationFromOtc = new Date(
        otc.starts_at.seconds * 1000 + otc.starts_at.nanoseconds / 1000000
    );

    if (!otc) return null;

    return (
        <>
            {loading && <Loader />}
            <DialogTitle>Edit One-Time Code</DialogTitle>
            <Divider />
            {!updatedOtc ? (
                <Formik
                    initialValues={{
                        reusable: otc?.reusable || false,
                        activation_date: activationFromOtc,
                        expiration_date: new Date(
                            otc.expires_at.seconds * 1000 +
                                otc.expires_at.nanoseconds / 1000000
                        )
                    }}
                    onSubmit={submit}
                    validationSchema={validationSchema}
                    validateOnChange
                >
                    {({ errors, handleSubmit, setFieldValue, touched, values }) => {
                        const minActivationDate =
                            getMinimumSelectableDate(activationFromOtc);

                        const minExpirationDate = new Date(values.activation_date);
                        minExpirationDate.setHours(minExpirationDate.getHours() + 1);

                        return (
                            <>
                                <DialogContent sx={{ p: 2.5, minHeight: 250 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={12}>
                                            <Typography
                                                variant="h3"
                                                fontWeight={700}
                                                component="div"
                                                color="secondary"
                                            >
                                                {otc.name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <Stack>
                                                <InputLabel>
                                                    Activation Date & Time
                                                </InputLabel>
                                                <FormControl
                                                    sx={{ width: '100%' }}
                                                    error={Boolean(
                                                        touched.activation_date &&
                                                            errors.activation_date
                                                    )}
                                                >
                                                    <LocalizationProvider
                                                        dateAdapter={AdapterDateFns}
                                                    >
                                                        <DateTimePicker
                                                            closeOnSelect={false}
                                                            value={values.activation_date}
                                                            onChange={newValue => {
                                                                setFieldValue(
                                                                    'activation_date',
                                                                    newValue
                                                                );
                                                            }}
                                                            minDateTime={
                                                                minActivationDate
                                                            }
                                                            minutesStep={1}
                                                        />
                                                    </LocalizationProvider>
                                                </FormControl>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <Stack spacing={0.5}>
                                                <Box>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={values.reusable}
                                                                onChange={() => {
                                                                    setFieldValue(
                                                                        'reusable',
                                                                        !values.reusable
                                                                    );
                                                                }}
                                                            />
                                                        }
                                                        label="Reusable Code"
                                                        labelPlacement="end"
                                                        sx={{ ml: 0 }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        {values.reusable && (
                                            <Grid item xs={12} sm={12}>
                                                <Stack>
                                                    <InputLabel>
                                                        Expiration Date & Time (Min 1 hour
                                                        after activation)
                                                    </InputLabel>
                                                    <FormControl
                                                        sx={{ width: '100%' }}
                                                        error={Boolean(
                                                            touched.expiration_date &&
                                                                errors.expiration_date
                                                        )}
                                                    >
                                                        <LocalizationProvider
                                                            dateAdapter={AdapterDateFns}
                                                        >
                                                            <DateTimePicker
                                                                closeOnSelect={false}
                                                                value={
                                                                    values.expiration_date
                                                                }
                                                                onChange={newValue =>
                                                                    setFieldValue(
                                                                        'expiration_date',
                                                                        newValue
                                                                    )
                                                                }
                                                                minDateTime={
                                                                    minExpirationDate
                                                                }
                                                                minutesStep={1}
                                                            />
                                                        </LocalizationProvider>
                                                        {touched.expiration_date &&
                                                            errors.expiration_date && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="error"
                                                                    sx={{
                                                                        mt: 0.5,
                                                                        ml: 1
                                                                    }}
                                                                >
                                                                    {
                                                                        errors.expiration_date
                                                                    }
                                                                </Typography>
                                                            )}
                                                    </FormControl>
                                                </Stack>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={12}>
                                            <Stack spacing={0.5}>
                                                <Typography
                                                    variant="h6"
                                                    color="secondary"
                                                >
                                                    * If the code is not reusable, the
                                                    expiration date will be 30 days from
                                                    the activation date.
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    color="secondary"
                                                >
                                                    ** This feature may not be available
                                                    at your site yet. Please contact SAGE
                                                    Support to enable it.
                                                </Typography>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </DialogContent>
                                <Divider />
                                <DialogActions sx={{ p: 2.5 }}>
                                    <Grid
                                        container
                                        justifyContent="right"
                                        alignItems="center"
                                    >
                                        <Grid item>
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                alignItems="center"
                                            >
                                                <Button color="error" onClick={onClose}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    onClick={handleSubmit}
                                                >
                                                    Edit
                                                </Button>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </DialogActions>
                            </>
                        );
                    }}
                </Formik>
            ) : (
                <>
                    <DialogContent sx={{ p: 2.5, minHeight: 250 }}>
                        <DisplayOtc otc={updatedOtc} />
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Grid container justifyContent="center" alignItems="center">
                            <AnimateButton>
                                <Button variant="contained" onClick={onClose}>
                                    Complete
                                </Button>
                            </AnimateButton>
                        </Grid>
                    </DialogActions>
                </>
            )}
        </>
    );
};

export default EditOtcModal;
